/**
 * Vue « DNS et salaires » — conception S158, décisions 125 et 128 à 132 du TL.
 *
 * Pour chaque entreprise adhérente : le dernier DNS appliqué de chacun de ses
 * contrats, l'effectif et les ayants droit qu'il couvre, la masse salariale
 * mensuelle qu'il déclare. Une lecture : aucun calcul de cotisation.
 *
 * Les montants circulent en chaînes décimales (`"1033180.50"`), jamais en
 * `number` : une somme de salaires ne doit pas perdre de centimes.
 */
import { z } from 'zod';
import { STATUTS_ADHERENT } from '../rules/eligibilite.js';
import { ORDRES_DE_TRI, schemaPage, schemaPagination } from './administration.js';
import { MODES_IMPORT_DNS } from './dns.js';
/** Un montant en ariary, arrondi au centime : `"350000.00"`. */
export const schemaMontantDecimal = z.string().regex(/^-?\d+\.\d{2}$/);
/**
 * La situation DNS d'une entreprise, une seule, dans cet ordre de priorité :
 * un DNS reçu attend la chargée ; aucun DNS appliqué ; le dernier est plus
 * ancien que le délai d'alerte de RG-PA-06 (décision 125) ; à jour.
 */
export const SITUATIONS_DNS = ['EN_ATTENTE', 'AUCUN', 'ANCIEN', 'A_JOUR'];
export const LIBELLE_SITUATION_DNS = {
    EN_ATTENTE: 'DNS en attente',
    AUCUN: 'Aucun DNS',
    ANCIEN: 'DNS ancien',
    A_JOUR: 'À jour',
};
export const TRIS_DNS_SALAIRES = [
    'nom',
    'code',
    'salaries',
    'ayantsDroit',
    'masse',
    'dernierDns',
    'situation',
];
/** `SANS` : un DNS appliqué sans aucun salaire lisible ; `BASE_SEULEMENT` : décision 79. */
export const FILTRES_SALAIRES = ['SANS', 'BASE_SEULEMENT'];
export const schemaFiltresDnsSalaires = schemaPagination.extend({
    limite: z.coerce.number().int().min(1).max(100).default(50),
    /** Code, raison sociale, NIF ou alias — sans égard à la casse. */
    recherche: z.string().trim().max(120).optional(),
    /** Absent : toutes les entreprises sauf les résiliées. */
    statut: z.enum(STATUTS_ADHERENT).optional(),
    situation: z.enum(SITUATIONS_DNS).optional(),
    offreId: z.uuid().optional(),
    salaires: z.enum(FILTRES_SALAIRES).optional(),
    tri: z.enum(TRIS_DNS_SALAIRES).default('nom'),
    ordre: z.enum(ORDRES_DE_TRI).default('asc'),
});
export const schemaOffreDeLigne = z.object({
    id: z.uuid(),
    code: z.string(),
    nom: z.string(),
});
export const schemaLigneDnsSalaires = z.object({
    id: z.uuid(),
    code: z.string(),
    nom: z.string(),
    statut: z.enum(STATUTS_ADHERENT),
    /** Les offres des contrats actifs, de la plus basse à la plus haute. */
    offres: z.array(schemaOffreDeLigne),
    /**
     * Le dernier DNS appliqué : la plus récente des périodes déclarées et des
     * dates d'application de ses contrats. `null` : aucun DNS appliqué.
     */
    dernierDns: z
        .object({
        /** `AAAA-MM-JJ` ; `null` : la période n'est pas dite. */
        periode: z.string().nullable(),
        appliqueLe: z.string(),
    })
        .nullable(),
    /** Réception du dernier DNS (RG-PA-06), appliqué ou non. */
    dernierDnsRecuLe: z.string().nullable(),
    /** Salariés couverts par le dernier DNS appliqué de chaque contrat. `null` : aucun DNS. */
    salaries: z.number().int().nullable(),
    /** Conjoints et enfants de ces salariés. `null` : aucun DNS. */
    ayantsDroit: z.number().int().nullable(),
    /** Somme des salaires du dernier DNS, ramenée au mois. `null` : aucun salaire lisible. */
    masseMensuelle: schemaMontantDecimal.nullable(),
    /** Lignes de salaire qui font cette masse. */
    nbSalaires: z.number().int(),
    /** Un DNS trimestriel : ses montants ont été divisés par trois. */
    trimestriel: z.boolean(),
    /** Le fichier ne donnait que le salaire de base (décision 79). */
    baseSeulement: z.boolean(),
    /**
     * Décision 227 — la période du dernier import « embauches et départs »
     * appliqué après la dernière liste complète, `AAAA-MM-JJ` : effectif et
     * masse restent ceux de la liste complète, ces mouvements n'y sont pas.
     * `null` : aucun.
     */
    mouvementsNonIntegres: z.string().nullable(),
    situation: z.enum(SITUATIONS_DNS),
});
export const schemaTotauxDnsSalaires = z.object({
    entreprises: z.number().int(),
    salaries: z.number().int(),
    ayantsDroit: z.number().int(),
    /**
     * Somme des dernières masses mensuelles déclarées — pas la masse d'un mois
     * donné : les entreprises n'ont pas toutes déclaré la même période.
     */
    masseMensuelle: schemaMontantDecimal,
    parSituation: z.object({
        EN_ATTENTE: z.number().int(),
        AUCUN: z.number().int(),
        ANCIEN: z.number().int(),
        A_JOUR: z.number().int(),
    }),
});
export const schemaPageDnsSalaires = schemaPage(schemaLigneDnsSalaires).extend({
    /** Sur tout l'ensemble filtré, pas sur la page affichée. */
    totaux: schemaTotauxDnsSalaires,
    /** Le délai d'alerte de RG-PA-06 qui fait un DNS « ancien », en jours. */
    delaiAlerteJours: z.number().int(),
});
// ─────────────────────────────────────────────────────────────────────
//  Historique d'une entreprise et aperçu d'un import (décisions 128, 131)
// ─────────────────────────────────────────────────────────────────────
/**
 * Colonnes triables de la liste des imports (demande du TL, 22/10). Le tri se
 * fait en base : la liste est paginée, trier la page seule mentirait.
 * Par défaut, du plus récemment appliqué au plus ancien.
 */
export const TRIS_HISTORIQUE_DNS = ['mois', 'applique', 'salaries', 'ayantsDroit', 'masse'];
export const schemaFiltresHistoriqueDns = schemaPagination.extend({
    limite: z.coerce.number().int().min(1).max(100).default(50),
    tri: z.enum(TRIS_HISTORIQUE_DNS).default('applique'),
    ordre: z.enum(ORDRES_DE_TRI).default('desc'),
});
export const schemaImportHistorique = z.object({
    id: z.uuid(),
    /** `null` : un ancien import, pour toute l'entreprise (avant CP-08). */
    contratId: z.uuid().nullable(),
    nomFichier: z.string().nullable(),
    /** Décision 227 — un import d'embauches et départs ne fait jamais foi pour l'effectif ni la masse. */
    mode: z.enum(MODES_IMPORT_DNS),
    statut: z.enum(['APPLIQUE', 'ANNULE']),
    /** Le mois déclaré, `AAAA-MM-JJ` ; `null` : non dit. */
    periode: z.string().nullable(),
    recuLe: z.string().nullable(),
    appliqueLe: z.string(),
    annuleLe: z.string().nullable(),
    /** Le dernier DNS appliqué de son contrat : celui que la vue additionne. */
    faitFoi: z.boolean(),
    /** Figés à l'application ; `null` pour un ancien import qui ne fait plus foi. */
    salaries: z.number().int().nullable(),
    ayantsDroit: z.number().int().nullable(),
    /**
     * Appliqué avant que la couverture soit figée : recalculée sur l'état
     * d'aujourd'hui, et sur TOUS les salariés actifs du contrat — un salarié
     * absent du DNS mais resté actif y compte, alors qu'une couverture figée ne
     * compte que les salariés que le DNS cite (revue S186, C4).
     */
    recalcule: z.boolean(),
    /** La masse de sa période déclarée, ramenée au mois ; `null` : aucun salaire (ou annulé). */
    masseMensuelle: schemaMontantDecimal.nullable(),
    nbSalaires: z.number().int(),
    trimestriel: z.boolean(),
    baseSeulement: z.boolean(),
});
export const schemaContratHistorique = z.object({
    id: z.uuid(),
    offre: schemaOffreDeLigne,
    populationLibelle: z.string().nullable(),
    actif: z.boolean(),
    /** La souscription — déclarative : le DNS fait foi (RG-PA-04). */
    effectifDeclare: z.number().int().nullable(),
    masseSalarialeAnnuelle: schemaMontantDecimal.nullable(),
});
export const schemaHistoriqueDns = schemaPage(schemaImportHistorique).extend({
    entreprise: z.object({ id: z.uuid(), code: z.string(), nom: z.string() }),
    contrats: z.array(schemaContratHistorique),
});
/**
 * Colonnes triables de l'aperçu d'un import (demande du TL, 22/10), en base
 * pour la même raison. `brut` : le salaire tel que déclaré ; `mensuel` : ramené au mois.
 */
export const TRIS_SALAIRES_IMPORT = ['nom', 'matricule', 'periode', 'brut', 'mensuel'];
export const schemaFiltresSalairesImport = schemaPagination.extend({
    limite: z.coerce.number().int().min(1).max(100).default(50),
    /** Nom ou matricule — sans égard à la casse. */
    recherche: z.string().trim().max(120).optional(),
    /** Une seule période (`AAAA-MM-JJ`) d'un classeur mensuel. */
    /** Une date réelle : `2026-02-30` est refusée en 400, pas en 500 (revue S186, C3). */
    periode: z.iso.date().optional(),
    tri: z.enum(TRIS_SALAIRES_IMPORT).default('nom'),
    ordre: z.enum(ORDRES_DE_TRI).default('asc'),
});
export const schemaSalairesImport = schemaPage(z.object({
    personneId: z.uuid(),
    nom: z.string(),
    prenom: z.string().nullable(),
    matricule: z.string().nullable(),
    periode: z.string().nullable(),
    moisCouverts: z.number().int(),
    /** Tel que déclaré, pour `moisCouverts` mois. */
    salaireBrut: schemaMontantDecimal,
    salaireMensuel: schemaMontantDecimal,
    baseSeulement: z.boolean(),
})).extend({
    import: schemaImportHistorique.extend({
        entreprise: z.object({ id: z.uuid(), code: z.string(), nom: z.string() }),
    }),
    /** Chaque période de l'import, la plus récente d'abord. */
    periodes: z.array(z.object({
        periode: z.string().nullable(),
        moisCouverts: z.number().int(),
        nbSalaires: z.number().int(),
        masseMensuelle: schemaMontantDecimal,
        baseSeulement: z.boolean(),
    })),
});
//# sourceMappingURL=dns-salaires.js.map